import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import Landing from "./pages/Landing";
import Auth from "./pages/Auth";
import DashboardLayout from "./layouts/DashboardLayout";
import AdminLayout from "./layouts/AdminLayout";
import Dashboard from "./pages/Dashboard";
import Staff from "./pages/dashboard/Staff";
import Children from "./pages/dashboard/Children";
import Classes from "./pages/dashboard/Classes";
import Lessons from "./pages/dashboard/Lessons";
import LessonDetail from "./pages/dashboard/LessonDetail";
import TakeExam from "./pages/dashboard/TakeExam";
import ExamSubmissions from "./pages/dashboard/ExamSubmissions";
import Reports from "./pages/dashboard/Reports";
import Parents from "./pages/dashboard/Parents";
import ChildAnalytics from "./pages/dashboard/ChildAnalytics";
import LiveSessions from "./pages/dashboard/LiveSessions";
import InteractiveLearning from "./pages/dashboard/InteractiveLearning";
import GameReports from "./pages/dashboard/GameReports";
import SettingsPage from "./pages/dashboard/Settings";
import Attendance from "./pages/dashboard/Attendance";
import Billing from "./pages/dashboard/Billing";
import SalaryReport from "./pages/dashboard/SalaryReport";
import FinancialLedger from "./pages/dashboard/FinancialLedger";
import ParentDashboard from "./pages/dashboard/ParentDashboard";
import ParentModeSelector from "./pages/dashboard/ParentModeSelector";
import StudentDashboard from "./pages/dashboard/StudentDashboard";
import CartoonGames from "./pages/dashboard/CartoonGames";
import Assignments from "./pages/dashboard/Assignments";
import ChildLearning from "./pages/dashboard/ChildLearning";
import Subscription from "./pages/dashboard/Subscription";
import ChildObservations from "./pages/dashboard/ChildObservations";
import Activities from "./pages/dashboard/Activities";
import Backup from "./pages/dashboard/Backup";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminNurseries from "./pages/admin/AdminNurseries";
import AdminSubscriptions from "./pages/admin/AdminSubscriptions";
import AdminUsers from "./pages/admin/AdminUsers";
import AdminPermissions from "./pages/admin/AdminPermissions";
import AdminStaff from "./pages/admin/AdminStaff";
import AdminBilling from "./pages/admin/AdminBilling";
import AdminBroadcast from "./pages/admin/AdminBroadcast";
import AdminSupport from "./pages/admin/AdminSupport";
import AdminPromoCodes from "./pages/admin/AdminPromoCodes";
import AdminSettings from "./pages/admin/AdminSettings";
import AdminSystemTest from "./pages/admin/AdminSystemTest";
import AdminActivity from "./pages/admin/AdminActivity";
import { useActivityTracker } from "./hooks/useActivityTracker";
import SupportPage from "./pages/dashboard/Support";
import SupportLayout from "./layouts/SupportLayout";
import SupportDashboard from "./pages/support/SupportDashboard";
import SupportTickets from "./pages/support/SupportTickets";
import SupportCalls from "./pages/support/SupportCalls";
import SupportUsers from "./pages/support/SupportUsers";
import SupportStats from "./pages/support/SupportStats";
import AccountantLayout from "./layouts/AccountantLayout";
import AccountantDashboard from "./pages/accountant/AccountantDashboard";
import AccountantInvoices from "./pages/accountant/AccountantInvoices";
import AccountantSubscriptions from "./pages/accountant/AccountantSubscriptions";
import AccountantReports from "./pages/accountant/AccountantReports";
import AccountantSubscriptionInvoices from "./pages/accountant/AccountantSubscriptionInvoices";
import AccountantPlanPricing from "./pages/accountant/AccountantPlanPricing";
import AccountantMonthlyStatus from "./pages/accountant/AccountantMonthlyStatus";
import AccountantAlertsLog from "./pages/accountant/AccountantAlertsLog";
import NotFound from "./pages/NotFound";
import Install from "./pages/Install";

const queryClient = new QueryClient();

const AppRoutes = () => {
  useActivityTracker();
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/auth" element={<Auth />} />
      <Route path="/install" element={<Install />} />
            <Route path="/dashboard" element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
              <Route index element={<Dashboard />} />
              <Route path="staff" element={<Staff />} />
              <Route path="children" element={<Children />} />
              <Route path="classes" element={<Classes />} />
              <Route path="lessons" element={<Lessons />} />
              <Route path="lesson/:lessonId" element={<LessonDetail />} />
              <Route path="assignments" element={<Assignments />} />
              <Route path="exam/:examId/take" element={<TakeExam />} />
              <Route path="exam/:examId/results" element={<ExamSubmissions />} />
              <Route path="reports" element={<Reports />} />
              <Route path="parents" element={<Parents />} />
              <Route path="analytics" element={<ChildAnalytics />} />
              <Route path="attendance" element={<Attendance />} />
              <Route path="live-sessions" element={<LiveSessions />} />
              <Route path="interactive-learning" element={<InteractiveLearning />} />
              <Route path="game-reports" element={<GameReports />} />
              <Route path="billing" element={<Billing />} />
              <Route path="salary-report" element={<SalaryReport />} />
              <Route path="financial-ledger" element={<FinancialLedger />} />
              <Route path="parent" element={<ParentModeSelector />} />
              <Route path="parent-home" element={<ParentDashboard />} />
              <Route path="student/:childId" element={<StudentDashboard />} />
              <Route path="student/:childId/cartoon-games" element={<CartoonGames />} />
              <Route path="child/:childId" element={<ChildLearning />} />
              <Route path="subscription" element={<Subscription />} />
              <Route path="observations" element={<ChildObservations />} />
              <Route path="activities" element={<Activities />} />
              <Route path="support" element={<SupportPage />} />
              <Route path="backup" element={<Backup />} />
              <Route path="settings" element={<SettingsPage />} />
            </Route>
            <Route path="/admin" element={<ProtectedRoute><AdminLayout /></ProtectedRoute>}>
              <Route index element={<AdminDashboard />} />
              <Route path="nurseries" element={<AdminNurseries />} />
              <Route path="subscriptions" element={<AdminSubscriptions />} />
              <Route path="users" element={<AdminUsers />} />
              <Route path="permissions" element={<AdminPermissions />} />
              <Route path="staff" element={<AdminStaff />} />
              <Route path="billing" element={<AdminBilling />} />
              <Route path="broadcast" element={<AdminBroadcast />} />
              <Route path="support" element={<AdminSupport />} />
              <Route path="promo-codes" element={<AdminPromoCodes />} />
              <Route path="settings" element={<AdminSettings />} />
              <Route path="system-test" element={<AdminSystemTest />} />
              <Route path="activity" element={<AdminActivity />} />
            </Route>
            <Route path="/support-panel" element={<ProtectedRoute><SupportLayout /></ProtectedRoute>}>
              <Route index element={<SupportDashboard />} />
              <Route path="tickets" element={<SupportTickets />} />
              <Route path="users" element={<SupportUsers />} />
              <Route path="calls" element={<SupportCalls />} />
              <Route path="stats" element={<SupportStats />} />
            </Route>
            <Route path="/accountant" element={<ProtectedRoute><AccountantLayout /></ProtectedRoute>}>
              <Route index element={<AccountantDashboard />} />
              <Route path="invoices" element={<AccountantInvoices />} />
              <Route path="subscriptions" element={<AccountantSubscriptions />} />
              <Route path="subscription-invoices" element={<AccountantSubscriptionInvoices />} />
              <Route path="monthly-status" element={<AccountantMonthlyStatus />} />
              <Route path="alerts-log" element={<AccountantAlertsLog />} />
              <Route path="plan-pricing" element={<AccountantPlanPricing />} />
              <Route path="reports" element={<AccountantReports />} />
            </Route>
            <Route path="*" element={<NotFound />} />
    </Routes>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
      </TooltipProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
